import React, { useState } from 'react'
import _ from 'lodash'
import classNames from 'classnames'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { Edge } from 'shared-libs/generated/server-types/entity'
import { DateTimeWithTimezone } from 'shared-libs/generated/server-types/entity/locationEntity'
import { Event } from 'shared-libs/models/types/storyboard/storyboard-execution-model'
import { ElementType } from 'shared-libs/helpers/types'
import { Entity } from 'shared-libs/models/entity'
import apis from 'browser/app/models/apis'
import { PromiseItem } from '../../promise-item'
import { EntityPreviewInline } from './entity-preview-inline'
import { formatFeedDateTime } from '../util'

export const EventChanges: React.FC<{ children?: React.ReactNode }> = (props) => {
  const [expanded, setExpanded] = useState(false)

  const handleToggleExpanded = () => {
    setExpanded(!expanded)
  }

  const count = React.Children.count(props.children)

  if (!count) {
    return null
  } else if (count === 1) {
    return <>{props.children}</>
  } else {
    return (
      <>
        <button className="c-timelineEvent-body-accordion" onClick={handleToggleExpanded}>
          <Icon
            icon={expanded ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_RIGHT}
            className="c-timelineEvent-body-accordion__icon"
          />
        </button>
        {!expanded && <strong>Show {count} changes</strong>}
        {expanded && props.children}
      </>
    )
  }
}

export type EventMapping = ElementType<Event['outputMappings']>

interface IEventChangeProps {
  mapping: EventMapping
  workflowEntity: Entity
  index: any
}

function findLabel(entity: Entity, path: string | undefined): string | undefined {
  if (!path) {
    return undefined
  }

  const splitPath = path.split('.')

  while (splitPath.length > 0) {
    const schema = entity.resolveSubschemaByValuePath(splitPath.join('.'))

    if (schema?.schema.label) {
      return schema.schema.label
    }

    splitPath.pop()
  }

  if (path.includes('core_yms_execution')) {
    const lastPortion = path.split('.').pop()
    if (lastPortion !== undefined) {
      const wordSplitLastPortion = lastPortion.split(/(?<=[a-z])(?=[A-Z])/)
      return wordSplitLastPortion.map(_.capitalize).join(' ')
    }
  }
}

export function renderPrettyValue(
  propSchema: ReturnType<Entity['resolveSubschemaByValuePath']>,
  key: string | undefined,
  value: EventMapping['value']
) {
  if (_.isNil(value)) {
    return undefined
  }

  if (propSchema?.schema.format === 'date-time' && _.isString(value)) {
    return formatFeedDateTime(value)
  }
  if (_.endsWith(key, '.entityId')) {
    const promise = apis.getStore().getOrFetchRecord(value)
    return <PromiseItem promise={promise} mapper={(entity) => entity.displayName} />
  }

  if (_.isObject(value)) {
    const dateTime = getDateTime(value)
    if (dateTime) {
      return formatFeedDateTime(dateTime)
    }

    const inlineEntity = renderEntityPreview(value)
    if (inlineEntity) {
      return inlineEntity
    }

    const scalarSummary = summarizeScalarObject(value)
    if (scalarSummary) {
      return scalarSummary
    }

    return JSON.stringify(value)
  }

  return value.toString()
}

export const renderEventChange: React.FC<IEventChangeProps> = (props) => {
  const { mapping, workflowEntity, index } = props
  const { destination: key, value } = mapping

  const propSchema = workflowEntity.resolveSubschemaByValuePath(key)
  const propLabel = findLabel(workflowEntity, key)

  const prettyValue = renderPrettyValue(propSchema, key, value)
  if (_.isNil(value) || _.isNil(key) || propSchema?.schema.shouldHideFromFeed) {
    return null
  } else if (propLabel === undefined && key.includes('sharedContext')) {
    return null
  } else if (_.isObject(value)) {
    const parsedValue = renderGroupedValue(prettyValue)
    if (!parsedValue) {
      return null
    }
    return (
      <div key={key}>
        Set <strong>{propLabel || key}</strong> to {parsedValue}
      </div>
    )
  } else if (_.isNil(prettyValue)) {
    return (
      <div key={key}>
        Removed <strong>{propLabel || key}</strong>
      </div>
    )
  } else {
    return (
      <div key={key}>
        Set <strong>{propLabel || key}</strong> to <strong>{prettyValue}</strong>
      </div>
    )
  }
}

function renderGroupedValue(prettyValue: ReturnType<typeof renderPrettyValue>) {
  if (_.isNil(prettyValue)) {
    return undefined
  }

  if (React.isValidElement(prettyValue)) {
    return prettyValue
  }

  return <strong>{prettyValue}</strong>
}

function getDateTime(value: Partial<DateTimeWithTimezone>) {
  return value.dateTime
}

function renderEntityPreview(value: Partial<Edge>) {
  if (!value?.entityId) {
    return undefined
  }

  return <EntityPreviewInline entityId={value.entityId} text={value.displayName} entity={value} />
}

function summarizeScalarObject(value: {}) {
  if (!_.isObject(value)) {
    return undefined
  }

  const scalarEntries = Object.entries(value).filter(([key, val]) => {
    return !_.isNil(val) && !_.isObject(val)
  })

  if (!scalarEntries.length) {
    return undefined
  }

  return scalarEntries.map(([k, v]) => `${_.startCase(k)}: ${v}`).join(', ')
}
